import { useEffect, useState } from 'react';

/** Dispatched when a uid's admin status is resolved or cleared. */
export const ADMIN_STATUS_EVENT = 'cfa-admin-status';

const cache = new Map(); // uid -> boolean
const inflight = new Map(); // uid -> Promise<boolean>
const listeners = new Set();

function notify() {
  listeners.forEach((cb) => {
    try {
      cb();
    } catch {
      /* ignore listener errors */
    }
  });
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new Event(ADMIN_STATUS_EVENT));
  }
}

export function subscribeAdminStatus(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function clearAdminStatusCache(uid) {
  if (uid) {
    cache.delete(uid);
    inflight.delete(uid);
  } else {
    cache.clear();
    inflight.clear();
  }
  notify();
}

/** Sync: cached result for this uid only (false until fetchIsAdmin resolves). */
export function isAdminUser(user) {
  if (!user?.uid) return false;
  return cache.get(user.uid) === true;
}

/**
 * Ask the server whether this Firebase user is an admin.
 * Memoized per uid on authoritative 200 JSON; in-flight requests are deduped.
 * Transport / non-2xx / non-JSON → false for this call only (not cached).
 */
export async function fetchIsAdmin(user) {
  if (!user?.uid || typeof user.getIdToken !== 'function') return false;
  if (cache.has(user.uid)) return cache.get(user.uid);
  if (inflight.has(user.uid)) return inflight.get(user.uid);

  const pending = (async () => {
    try {
      const token = await user.getIdToken();
      const res = await fetch('/api/admin-status', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) return false;

      let data;
      try {
        data = await res.json();
      } catch {
        return false;
      }
      if (typeof data?.isAdmin !== 'boolean') return false;

      cache.set(user.uid, data.isAdmin);
      notify();
      return data.isAdmin;
    } catch {
      return false;
    } finally {
      inflight.delete(user.uid);
    }
  })();

  inflight.set(user.uid, pending);
  return pending;
}

/** Mark a profile row as admin without shipping an allowlist email. */
export function isAdminProfileRow(profile = {}, { uid = '', currentUser = null } = {}) {
  if (profile.isAdmin) return true;
  if (!currentUser) return false;
  const rowUid = uid || profile.id || profile.uid || '';
  if (rowUid && rowUid === currentUser.uid) return true;
  const pe = String(profile.email || '')
    .trim()
    .toLowerCase();
  const ue = String(currentUser.email || '')
    .trim()
    .toLowerCase();
  return Boolean(pe && ue && pe === ue);
}

/**
 * React hook: { isAdmin, loading }.
 * loading is true until the first resolution for the current uid.
 */
export function useIsAdmin(user) {
  const uid = user?.uid || '';
  const [isAdmin, setIsAdmin] = useState(() => isAdminUser(user));
  const [loading, setLoading] = useState(() => Boolean(uid) && !cache.has(uid));

  useEffect(() => {
    let cancelled = false;
    if (!uid) {
      setIsAdmin(false);
      setLoading(false);
      return undefined;
    }

    if (cache.has(uid)) {
      setIsAdmin(cache.get(uid) === true);
      setLoading(false);
    } else {
      setLoading(true);
    }

    fetchIsAdmin(user).then((value) => {
      if (!cancelled) {
        setIsAdmin(value);
        setLoading(false);
      }
    });

    const unsub = subscribeAdminStatus(() => {
      if (!cancelled) setIsAdmin(isAdminUser(user));
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, [uid, user]);

  return { isAdmin, loading };
}
